import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Trash2, Download, Calendar, Ruler, DollarSign,
  Image, ChevronRight, Wand2, Hand, FileText, Loader2
} from "lucide-react";
import { API } from "@/App";
import { generateQuotePDF } from "@/utils/generateQuotePDF";

export default function HistoryPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSim, setSelectedSim] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  useEffect(() => {
    fetchSimulations();
  }, []);

  const fetchSimulations = async () => {
    try {
      const res = await axios.get(`${API}/api/simulations`, { withCredentials: true });
      setSimulations(res.data);
    } catch {
      toast.error("Errore nel caricamento dello storico");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (simId) => {
    if (!window.confirm("Eliminare questa simulazione?")) return;
    try {
      await axios.delete(`${API}/api/simulations/${simId}`, { withCredentials: true });
      setSimulations(prev => prev.filter(s => s.simulation_id !== simId));
      if (selectedSim?.simulation_id === simId) setSelectedSim(null);
      toast.success("Simulazione eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handlePDF = async (sim, e) => {
    e.stopPropagation();
    setPdfLoadingId(sim.simulation_id);
    try {
      // Load result image as data URL if available
      let resultImage = null;
      if (sim.result_image_url) {
        const imgUrl = `${API}${sim.result_image_url}`;
        resultImage = await new Promise((resolve) => {
          const img = new window.Image();
          img.onload = () => {
            const c = document.createElement("canvas");
            c.width = img.width;
            c.height = img.height;
            c.getContext("2d").drawImage(img, 0, 0);
            try {
              resolve(c.toDataURL("image/jpeg", 0.8));
            } catch {
              resolve(null);
            }
          };
          img.onerror = () => resolve(null);
          img.src = imgUrl;
        });
      }

      await generateQuotePDF({
        wallpaper: { name: sim.wallpaper_name },
        collection: {
          name: sim.collection_name,
          collection_type: sim.collection_type || "baseline",
          price_per_sqm: sim.price_per_sqm,
        },
        resultImage,
        wallWidthCm: sim.wall_width_cm,
        wallHeightCm: sim.wall_height_cm,
        method: sim.method,
        user,
      });
      toast.success("PDF preventivo scaricato!");
    } catch (err) {
      console.error("PDF error:", err);
      toast.error("Errore nella generazione del PDF");
    } finally {
      setPdfLoadingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="history-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/catalogo")} className="flex items-center gap-2 text-stone-500 hover:text-stone-900">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest hidden sm:inline">Catalogo</span>
            </button>
            <ChevronRight className="w-3 h-3 text-stone-300" />
            <span className="text-sm font-medium text-stone-900">Storico Simulazioni</span>
          </div>
          <span className="text-xs text-stone-400">{simulations.length} simulazioni</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {simulations.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h2 className="text-lg font-light text-stone-700 mb-2">Nessuna simulazione</h2>
            <p className="text-sm text-stone-500 mb-6">Le tue simulazioni salvate appariranno qui</p>
            <Button onClick={() => navigate("/catalogo")} className="rounded-none bg-stone-900 text-white">
              Vai al Catalogo
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {simulations.map(sim => (
              <div
                key={sim.simulation_id}
                data-testid={`simulation-${sim.simulation_id}`}
                className={`bg-white border p-4 flex flex-col sm:flex-row gap-4 cursor-pointer transition-colors ${
                  selectedSim?.simulation_id === sim.simulation_id ? "border-stone-400" : "border-stone-200 hover:border-stone-300"
                }`}
                onClick={() => setSelectedSim(selectedSim?.simulation_id === sim.simulation_id ? null : sim)}
              >
                {/* Thumbnail */}
                <div className="w-full sm:w-32 h-24 bg-stone-100 flex-shrink-0 overflow-hidden">
                  {sim.result_image_url ? (
                    <img
                      src={`${API}${sim.result_image_url}`}
                      alt={sim.wallpaper_name}
                      className="w-full h-full object-cover"
                    />
                  ) : sim.wallpaper_image_url ? (
                    <img
                      src={sim.wallpaper_image_url}
                      alt={sim.wallpaper_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-6 h-6 text-stone-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-medium text-stone-900">{sim.wallpaper_name}</h3>
                      <p className="text-xs text-stone-500">{sim.collection_name}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {sim.method === "ai" ? (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-amber-50 text-amber-700 flex items-center gap-1">
                          <Wand2 className="w-3 h-3" /> AI
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-stone-100 text-stone-600 flex items-center gap-1">
                          <Hand className="w-3 h-3" /> Manuale
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-stone-500">
                    <span className="flex items-center gap-1">
                      <Ruler className="w-3 h-3" />
                      {sim.wall_width_cm} x {sim.wall_height_cm} cm ({sim.area_sqm} mq)
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      {sim.total_price?.toFixed(2).replace('.', ',')} € (no posa)
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(sim.created_at)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex sm:flex-col gap-2 flex-shrink-0">
                  <Button
                    data-testid={`pdf-sim-${sim.simulation_id}`}
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handlePDF(sim, e)}
                    disabled={pdfLoadingId === sim.simulation_id}
                    className="rounded-none text-amber-600 hover:text-amber-700 h-8"
                    title="Scarica PDF Preventivo"
                  >
                    {pdfLoadingId === sim.simulation_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <FileText className="w-4 h-4" />}
                  </Button>
                  <Button
                    data-testid={`delete-sim-${sim.simulation_id}`}
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(sim.simulation_id); }}
                    className="rounded-none text-stone-400 hover:text-red-600 h-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
